import { useMemo, useState } from 'react';
import { Check, ChevronRight, Minus, Search, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { SegmentedControl, type SegmentOption } from '@/components/ui/SegmentedControl';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import type { AccessPermission, GrantState } from '@/api/bi/access';
import {
  PERMISSIONS,
  changedSlugs,
  descendantsOf,
  resolveInherited,
  rowToggleState,
  type GrantDraft,
  type OrderedResource,
} from './grants';
import { cn } from '@/lib/cn';

const CELL_OPTIONS: SegmentOption<GrantState>[] = [
  { value: 'inherit', label: <Minus className="h-3 w-3" aria-hidden />, title: 'Inherit', tone: 'neutral' },
  { value: 'allow', label: <Check className="h-3 w-3" aria-hidden />, title: 'Allow', tone: 'success' },
  { value: 'deny', label: <X className="h-3 w-3" aria-hidden />, title: 'Deny', tone: 'danger' },
];

const ROW_OPTIONS: SegmentOption<'allow' | 'deny' | 'mixed'>[] = [
  { value: 'allow', label: 'Allow', tone: 'success' },
  { value: 'deny', label: 'Deny', tone: 'danger' },
];

export interface AccessMatrixProps {
  ordered: OrderedResource[];
  draft: GrantDraft;
  baseline: GrantDraft;
  cellDefault: GrantState;
  /** User mode shows what an inherited cell resolves to from the role. */
  mode: 'role' | 'user';
  roleGrantBySlug: Map<string, Partial<Record<AccessPermission, GrantState>>>;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onCell: (slug: string, permission: AccessPermission, state: GrantState) => void;
  onRow: (slugs: string[], state: 'allow' | 'deny') => void;
  onColumn: (permission: AccessPermission, state: GrantState, slugs: string[]) => void;
}

/**
 * The resource × permission grid.
 *
 * The version this replaces was a bare `<table>` inside an `overflow-x-auto`
 * div: no sticky header (so by row 30 you had no idea which column was which —
 * the reported problem), no sticky resource column (so scrolling right hid
 * *which* row you were editing), no search across 60+ rows, and a `<Select>` in
 * every one of the ~240 cells.
 *
 * Note on the sticky implementation: `overflow-x-auto` alone can't work,
 * because setting one overflow axis to `auto` forces the other to `auto` too,
 * which makes the page an invalid sticky container. The scroll container needs
 * its own height cap, and the table needs `border-separate` since
 * `border-collapse` drops borders on sticky cells.
 */
export function AccessMatrix({
  ordered,
  draft,
  baseline,
  cellDefault,
  mode,
  roleGrantBySlug,
  isLoading,
  isError,
  onRetry,
  onCell,
  onRow,
  onColumn,
}: AccessMatrixProps) {
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('all');
  const [changedOnly, setChangedOnly] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const dirty = useMemo(() => changedSlugs(draft, baseline), [draft, baseline]);

  const groups = useMemo(() => {
    const set = new Set<string>();
    for (const resource of ordered) if (resource.group) set.add(resource.group);
    return Array.from(set).sort();
  }, [ordered]);

  /** Slugs hidden because an ancestor is collapsed. */
  const hiddenByCollapse = useMemo(() => {
    const hidden = new Set<string>();
    for (const slug of collapsed) {
      for (const descendant of descendantsOf(slug, ordered)) hidden.add(descendant);
    }
    return hidden;
  }, [collapsed, ordered]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return ordered.filter((resource) => {
      if (hiddenByCollapse.has(resource.slug)) return false;
      if (group !== 'all' && resource.group !== group) return false;
      if (changedOnly && !dirty.has(resource.slug)) return false;
      if (!needle) return true;
      return (
        resource.name.toLowerCase().includes(needle) ||
        resource.slug.toLowerCase().includes(needle) ||
        (resource.path ?? '').toLowerCase().includes(needle)
      );
    });
  }, [ordered, hiddenByCollapse, group, changedOnly, dirty, search]);

  const visibleSlugs = useMemo(() => visible.map((r) => r.slug), [visible]);
  const hasParents = useMemo(() => ordered.some((r) => r.childSlugs.length > 0), [ordered]);

  function toggleCollapse(slug: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search resources…"
            aria-label="Search resources"
            className="h-8 pl-7 pr-7 text-xs"
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded text-muted-foreground hover:text-foreground focus-ring"
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          ) : null}
        </div>

        <Select value={group} onValueChange={setGroup}>
          <SelectTrigger className="h-8 w-40 text-xs" aria-label="Filter by group">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All groups</SelectItem>
            {groups.map((g) => (
              <SelectItem key={g} value={g} className="capitalize">
                {g}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          size="sm"
          variant={changedOnly ? 'primary' : 'outline'}
          onClick={() => setChangedOnly((v) => !v)}
          aria-pressed={changedOnly}
        >
          Changed only
          {dirty.size ? <Badge tone="neutral" className="!px-1.5">{dirty.size}</Badge> : null}
        </Button>

        {hasParents ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              setCollapsed((prev) =>
                prev.size ? new Set() : new Set(ordered.filter((r) => r.childSlugs.length).map((r) => r.slug)),
              )
            }
          >
            {collapsed.size ? 'Expand all' : 'Collapse all'}
          </Button>
        ) : null}

        <span className="ml-auto text-[11px] text-muted-foreground">
          {visible.length} of {ordered.length} resources
        </span>
      </div>

      {isError ? (
        <ErrorState onRetry={onRetry} />
      ) : isLoading ? (
        <div className="space-y-2 rounded-md border border-border p-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : ordered.length === 0 ? (
        <EmptyState
          title="No BI resources seeded"
          description="Run the backend seed command before assigning access."
        />
      ) : visible.length === 0 ? (
        <EmptyState
          title="No resources match"
          description="Try a different search term, group, or clear the changed-only filter."
        />
      ) : (
        <div className="max-h-[68vh] overflow-auto rounded-md border border-border">
          <table className="w-full min-w-[820px] border-separate border-spacing-0 text-xs">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky left-0 top-0 z-30 border-b border-r border-border bg-surface-elevated px-3 py-2 text-left font-medium uppercase tracking-wider text-muted-foreground"
                >
                  Resource
                </th>
                <th
                  scope="col"
                  className="sticky top-0 z-20 border-b border-border bg-surface-elevated px-3 py-2 text-left font-medium uppercase tracking-wider text-muted-foreground"
                >
                  Set row
                </th>
                {PERMISSIONS.map((permission) => (
                  <th
                    key={permission}
                    scope="col"
                    className="sticky top-0 z-20 border-b border-border bg-surface-elevated px-3 py-2 text-left font-medium uppercase tracking-wider text-muted-foreground"
                  >
                    <span className="block capitalize">{permission}</span>
                    <span className="mt-1 flex items-center gap-1 normal-case tracking-normal">
                      <ColumnBulkButton
                        label="Allow"
                        onClick={() => onColumn(permission, 'allow', visibleSlugs)}
                        title={`Allow ${permission} for all ${visible.length} visible rows`}
                      />
                      <ColumnBulkButton
                        label="Deny"
                        onClick={() => onColumn(permission, 'deny', visibleSlugs)}
                        title={`Deny ${permission} for all ${visible.length} visible rows`}
                      />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((resource) => {
                const rowState = rowToggleState(draft[resource.slug], cellDefault);
                const isDirty = dirty.has(resource.slug);
                const isCollapsed = collapsed.has(resource.slug);
                const subtree = resource.childSlugs.length
                  ? [resource.slug, ...descendantsOf(resource.slug, ordered)]
                  : [resource.slug];
                return (
                  <tr key={resource.slug}>
                    {/*
                     * The sticky cell has to be fully opaque to occlude the
                     * columns sliding under it, which rules out a translucent
                     * row tint for the changed state. A left accent plus the
                     * dot below carries it instead.
                     */}
                    <th
                      scope="row"
                      className={cn(
                        'sticky left-0 z-10 border-b border-r border-border bg-surface px-3 py-1.5 text-left font-normal',
                        isDirty && 'border-l-2 border-l-primary',
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-1.5" style={{ paddingLeft: resource.depth * 14 }}>
                        {resource.childSlugs.length ? (
                          <button
                            type="button"
                            onClick={() => toggleCollapse(resource.slug)}
                            aria-expanded={!isCollapsed}
                            aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${resource.name}`}
                            className="press shrink-0 rounded text-muted-foreground hover:text-foreground focus-ring"
                          >
                            <ChevronRight
                              className={cn('h-3.5 w-3.5 transition-transform duration-150', !isCollapsed && 'rotate-90')}
                              aria-hidden
                            />
                          </button>
                        ) : (
                          <span className="w-3.5 shrink-0" aria-hidden />
                        )}
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 truncate font-medium">
                            {resource.name}
                            {isDirty ? (
                              <span
                                className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                                aria-label="changed"
                              />
                            ) : null}
                          </p>
                          <p className="truncate text-[10px] text-muted-foreground">
                            <span className="font-mono">{resource.slug}</span>
                            <span className="ml-1 capitalize opacity-70">· {resource.resource_type}</span>
                          </p>
                        </div>
                      </div>
                    </th>

                    <td className="border-b border-border px-3 py-1.5">
                      <SegmentedControl
                        size="xs"
                        value={rowState}
                        options={ROW_OPTIONS}
                        ariaLabel={`All permissions for ${resource.name}`}
                        onChange={(next) => {
                          if (next === 'allow' || next === 'deny') onRow(subtree, next);
                        }}
                      />
                      <span className="mt-0.5 block text-[9px] text-muted-foreground">
                        {rowState === 'mixed' ? 'mixed' : null}
                        {resource.childSlugs.length
                          ? `${rowState === 'mixed' ? ' · ' : ''}+${resource.childSlugs.length} child${
                              resource.childSlugs.length === 1 ? '' : 'ren'
                            }`
                          : null}
                      </span>
                    </td>

                    {PERMISSIONS.map((permission) => {
                      const value = draft[resource.slug]?.[permission] ?? cellDefault;
                      const inheritedTo =
                        mode === 'user' && value === 'inherit'
                          ? resolveInherited(roleGrantBySlug, resource.slug, permission)
                          : null;
                      return (
                        <td key={permission} className="border-b border-border px-3 py-1.5">
                          <SegmentedControl
                            size="xs"
                            value={value}
                            options={CELL_OPTIONS}
                            ariaLabel={`${permission} for ${resource.name}`}
                            onChange={(next) => onCell(resource.slug, permission, next)}
                          />
                          {inheritedTo ? (
                            <span
                              className={cn(
                                'mt-0.5 block text-[9px]',
                                inheritedTo === 'allow' ? 'text-success' : 'text-muted-foreground',
                              )}
                            >
                              role: {inheritedTo}
                            </span>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ColumnBulkButton({
  label,
  title,
  onClick,
}: {
  label: string;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="press rounded border border-border px-1 py-0.5 text-[9px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-ring"
    >
      {label}
    </button>
  );
}
